export type OutcomeRegistryRow = {
  key: string;
  group: string;
  groupLabel?: string;
  groupTitle?: string;
  groupName?: string;
  label?: string;
  baseLocked?: boolean;
  baseIncluded?: boolean;
  baseSelectable?: boolean;
  componentsSelectable?: boolean;
  optionsFrom?: string;
  selectionMode?: string;
  selectionModeLabel?: string;
  selectionModeText?: string;
  selectionModeName?: string;
};

export type OutcomePolicyFormValue = {
  baseIncluded: boolean;
  baseLocked: boolean;
  selectedComponents: string[];
};

type TranslateFn = (key: string) => unknown;

const getText = (value: any): string => {
  const text = String(value ?? "").trim();
  return text;
};

const getTranslatedText = (
  t: TranslateFn | undefined,
  key: string,
  fallback: string,
): string => {
  if (!t || !key) return fallback;

  const translated = t(key);
  return translated && translated !== key ? String(translated) : fallback;
};

const getRegistryGroupLabel = (
  outcomeRegistry: any,
  groupKey: string,
  rowValue?: any,
): string => {
  const groupLabels = outcomeRegistry?.groupLabels;
  const label =
    rowValue?.groupLabel ??
    rowValue?.groupTitle ??
    rowValue?.groupName ??
    rowValue?.group ??
    groupLabels?.[groupKey] ??
    outcomeRegistry?.[`${groupKey}Label`] ??
    outcomeRegistry?.[`${groupKey}Title`] ??
    "";

  return label ? String(label) : "";
};

export const formatRegistryText = (value: any): string => {
  return String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getOutcomeRegistryRows = (
  outcomeRegistry: any,
): OutcomeRegistryRow[] => {
  const rows: OutcomeRegistryRow[] = [];
  const coreOutcomes = outcomeRegistry?.coreOutcomes;
  const customOutcomes = outcomeRegistry?.customOutcomes;

  if (coreOutcomes && typeof coreOutcomes === "object") {
    Object.entries(coreOutcomes as Record<string, any>).forEach(
      ([key, value]) => {
        const rowValue = value || {};
        rows.push({
          key,
          ...rowValue,
          group: getRegistryGroupLabel(
            outcomeRegistry,
            "coreOutcomes",
            rowValue,
          ),
        });
      },
    );
  }

  if (Array.isArray(customOutcomes)) {
    customOutcomes.forEach((value: any) => {
      if (!value?.key) return;
      rows.push({
        ...value,
        key: value.key,
        group: getRegistryGroupLabel(
          outcomeRegistry,
          "customOutcomes",
          value,
        ),
      });
    });
  }

  return rows;
};

const getOutcomeGroupKey = (
  outcomeRegistry: any,
  row: OutcomeRegistryRow,
): "coreOutcomes" | "customOutcomes" => {
  const coreOutcomes = outcomeRegistry?.coreOutcomes;
  const isCoreOutcome =
    !!row.key &&
    !!coreOutcomes &&
    typeof coreOutcomes === "object" &&
    Object.prototype.hasOwnProperty.call(coreOutcomes, row.key);

  return isCoreOutcome ? "coreOutcomes" : "customOutcomes";
};

const isDefaultGroupText = (text: string): boolean => {
  const normalized = text.toLowerCase().replace(/[\s_]+/g, "");
  return normalized === "coreoutcomes" || normalized === "customoutcomes";
};

export const getOutcomeGroupLabel = (
  outcomeRegistry: any,
  row: OutcomeRegistryRow,
  t?: TranslateFn,
): string => {
  const groupKey = getOutcomeGroupKey(outcomeRegistry, row);
  const fallback =
    groupKey === "coreOutcomes" ? "Core outcomes" : "Custom outcomes";
  const backendLabel =
    getText(row.group) ||
    getText(row.groupLabel) ||
    getText(row.groupTitle) ||
    getText(row.groupName);

  if (backendLabel && !isDefaultGroupText(backendLabel)) {
    return getTranslatedText(t, backendLabel, backendLabel);
  }

  return getTranslatedText(t, `payoutPolicy.${groupKey}`, fallback);
};

export const getOutcomeRegistryRowsForUi = (
  outcomeRegistry: any,
  t?: TranslateFn,
): OutcomeRegistryRow[] => {
  return getOutcomeRegistryRows(outcomeRegistry).map((row) => ({
    ...row,
    group: getOutcomeGroupLabel(outcomeRegistry, row, t),
  }));
};

export const getOutcomeSelectionModeLabel = (
  row: OutcomeRegistryRow,
  t?: TranslateFn,
): string => {
  const backendLabel =
    getText(row.selectionModeLabel) ||
    getText(row.selectionModeText) ||
    getText(row.selectionModeName);

  if (backendLabel) {
    return getTranslatedText(t, backendLabel, backendLabel);
  }

  if (!row.selectionMode) return "-";

  return getTranslatedText(
    t,
    `payoutPolicy.selectionModes.${row.selectionMode}`,
    formatRegistryText(row.selectionMode),
  );
};

export const getOutcomePolicyFormValue = (
  row: OutcomeRegistryRow,
  savedValue: any,
): OutcomePolicyFormValue => {
  const savedObject =
    savedValue && typeof savedValue === "object" && !Array.isArray(savedValue)
      ? savedValue
      : {};

  const baseIncluded =
    typeof savedObject.baseIncluded === "boolean"
      ? savedObject.baseIncluded
      : !!row.baseIncluded;

  const baseLocked =
    typeof row.baseLocked === "boolean"
      ? row.baseLocked
      : !!savedObject.baseLocked;

  const selectedComponents = Array.isArray(savedObject.selectedComponents)
    ? savedObject.selectedComponents
        .filter((value: any) => value !== undefined && value !== null)
        .map((value: any) => String(value))
    : [];

  return {
    ...savedObject,
    baseIncluded,
    baseLocked,
    selectedComponents,
  };
};

const getSavedOutcomeValue = (savedStepValue: any, outcomeKey: string) => {
  return savedStepValue?.outcomes?.[outcomeKey] ?? savedStepValue?.[outcomeKey];
};

const getByPath = (source: any, path: string): any => {
  if (!source || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) return source[path];
  return path
    .split(".")
    .reduce((acc: any, key) => (acc != null ? acc[key] : undefined), source);
};

const getSelectableComponentKeys = (
  row: OutcomeRegistryRow,
  formData: Record<string, any>,
): Set<string> | null => {
  const optionsFrom = String(row.optionsFrom || "").trim();
  if (!optionsFrom) return null;

  const dotIndex = optionsFrom.indexOf(".");
  if (dotIndex === -1) return null;

  const sourceStepId = optionsFrom.slice(0, dotIndex);
  const sourceFieldKey = optionsFrom.slice(dotIndex + 1);
  const sourceItems = getByPath(formData, sourceStepId);

  if (!Array.isArray(sourceItems)) return new Set<string>();

  const keys = new Set<string>();
  sourceItems.forEach((item: any) => {
    const rawValue = getByPath(item, sourceFieldKey);
    if (rawValue === undefined || rawValue === null || rawValue === "") return;
    keys.add(String(rawValue));
  });

  return keys;
};

const sanitizeOutcomePolicyFormValue = (
  row: OutcomeRegistryRow,
  savedValue: any,
  formData?: Record<string, any>,
): OutcomePolicyFormValue => {
  const policyValue = getOutcomePolicyFormValue(row, savedValue);

  if (!formData) return policyValue;

  if (!row.componentsSelectable) {
    return {
      ...policyValue,
      selectedComponents: [],
    };
  }

  const selectableComponentKeys = getSelectableComponentKeys(row, formData);
  if (selectableComponentKeys === null) return policyValue;

  return {
    ...policyValue,
    selectedComponents: policyValue.selectedComponents.filter((component) =>
      selectableComponentKeys.has(component),
    ),
  };
};

export const buildOutcomeRegistryFormData = (
  outcomeRegistry: any,
  savedStepValue: any = {},
  formData?: Record<string, any>,
): Record<string, any> => {
  const stepValue =
    savedStepValue && typeof savedStepValue === "object"
      ? { ...savedStepValue }
      : {};
  const outcomes: Record<string, OutcomePolicyFormValue> =
    stepValue.outcomes && typeof stepValue.outcomes === "object"
      ? { ...stepValue.outcomes }
      : {};

  const rows = getOutcomeRegistryRows(outcomeRegistry);

  rows.forEach((row) => {
    outcomes[row.key] = sanitizeOutcomePolicyFormValue(
      row,
      getSavedOutcomeValue(savedStepValue, row.key),
      formData,
    );
    delete stepValue[row.key];
  });

  return {
    ...stepValue,
    outcomes,
  };
};

export const applyOutcomeRegistryDefaults = (
  formData: Record<string, any>,
  steps: any[] = [],
): Record<string, any> => {
  const next = JSON.parse(JSON.stringify(formData || {}));

  steps.forEach((step: any) => {
    if (!step?.stepId || !step?.outcomeRegistry) return;
    next[step.stepId] = buildOutcomeRegistryFormData(
      step.outcomeRegistry,
      next[step.stepId],
      next,
    );
  });

  return next;
};